import React, { useCallback, useMemo } from 'react';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ListItemIcon, ListItemText, MenuItem, Tooltip } from '@mui/material';
import { ProfileAttributes } from '@loyalty/shared-common/profile-api-client';
import { QlfConfirmationDialogProps, useAlerts } from '@qualifio/ui-commons';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useFeatures } from '../../../features/FeaturesContext';

interface Props {
  profile: ProfileAttributes;
  programId: string;
  onButtonClick: () => void;
  confirmationDialogState: QlfConfirmationDialogProps;
  setConfirmationDialogState: (props: QlfConfirmationDialogProps) => void;
  onDelete: () => void;
}

export function DeleteMember({
  profile,
  programId,
  onButtonClick,
  confirmationDialogState,
  setConfirmationDialogState,
  onDelete,
}: Props) {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const { features } = useFeatures();
  const { enabled } = features[Feature.DELETE_MEMBER];

  const { profileApi } = useApi();

  const displayName = useMemo(() => `${profile?.firstName} ${profile?.lastName}`, [profile]);

  const deleteMember = useCallback(async () => {
    try {
      await profileApi.deleteMember(programId, profile.id);
      notify(t('profiles.actions.delete.success', { member: displayName }), 'success');
      await onDelete?.();
    } catch (error) {
      console.error(error);
      notify(t('generic.error', { error }), 'error');
    }
  }, [profile, programId, onDelete, displayName]);

  const showDialog = () => {
    setConfirmationDialogState({
      ...confirmationDialogState,
      open: true,
      color: 'primary',
      title: t('profiles.actions.delete.confirm.title', { member: displayName }),
      content: t('profiles.actions.delete.confirm.message', { member: displayName }),
      confirmButtonColor: 'error',
      confirmButtonText: t('profiles.actions.delete.confirm.button'),
      cancelButtonText: t('publish.confirm.button.cancel'),
      onConfirm: deleteMember,
    });
    onButtonClick();
  };

  return (
    <Tooltip title={enabled ? '' : t('shared.action.permission.required')}>
      <div>
        <MenuItem onClick={showDialog}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faTrash} />
          </ListItemIcon>
          <ListItemText>{t('profiles.actions.delete.button')}</ListItemText>
        </MenuItem>
      </div>
    </Tooltip>
  );
}
